/**
 * Payment Gateway v1 — the frontend mirror of the wire contract.
 *
 * Source of truth: `backend/modules/gateway/schemas.py` +
 * `docs/payment-module/gateway-api-contract.md`. A change lands in all three artefacts
 * in one commit or it is not a change.
 *
 * Components and hooks consume the injected `gatewayApi` module (see `./api.js`), never
 * axios directly. Until integration its implementation is the `mock*` factories below,
 * so the Frontend workstream builds and tests against exactly the shapes the Backend
 * workstream returns. Every factory takes an overrides object so a test can pin one
 * field without restating the payload.
 */

// ── enums (mirror schemas.py) ───────────────────────────────────────────────────
export const PAYMENT_STATES = [
  "awaiting_payment_method", "awaiting_authorisation", "card_saved", "authorising",
  "auth_failed", "requires_capture", "capturing", "captured", "partially_captured",
  "settled", "cancelling", "cancelled", "expired", "failed", "refunded",
  "partially_refunded", "disputed",
];

// Mirrors `schemas.DEFAULT_SETTLEMENT_CURRENCY`. ONE constant, for the same reason it is
// one constant on the backend: the platform Stripe account decides it (§16.1 — Canadian
// since 2026-10-09, hence CAD; it was USD under the earlier US-entity decision), and every
// fixture that spelled it out was a place the next change would be forgotten. Fixtures use
// this; components read `currency` off the payload and never assume.
export const SETTLEMENT_CURRENCY = "cad";

export const LEDGER_ACCOUNTS = [
  "platform_cash", "tenant_payable", "platform_revenue_commission",
  "platform_revenue_subscription", "stripe_fees", "refunds_payable", "dispute_losses",
  "tenant_adjustments", "platform_adjustment_expense", "platform_equity_opening",
  // Stored value sold and not yet spent: cash the platform holds for a service nobody has
  // delivered. Credited when a gift card is bought, discharged inside the redeeming
  // order's own capture group.
  "giftcard_liability",
  // THE AFFILIATE PROGRAM'S PAIR (`modules/affiliate/schemas.py` owns both strings;
  // `src/affiliate/contract.js` mirrors them as `AFFILIATE_LEDGER_ACCOUNTS`). Spelled
  // here as literals rather than spread from that module because this array IS the
  // mirror the backend pin reads — `test_gateway_frontend_constants_pinned.py` scans
  // the source for quoted names, and an identifier it cannot resolve would read as an
  // account silently missing.
  //
  // `affiliate_payable` is TENANT-SCOPED and deliberately not `tenant_payable`: an
  // affiliate's earnings are a cut of somebody else's sale and have no payment of their
  // own. `platform_affiliate_expense` is debit-normal, so
  // `platform_revenue_commission` stays stated GROSS and the cost of the program sits
  // on its own line.
  "affiliate_payable", "platform_affiliate_expense",
];

export const EVIDENCE_GRADES = ["server_events", "join_and_heartbeat", "declared"];

// Mirrors `schemas.SESSION_STATES` (Flow C consultation lifecycle, contract §7). SEVEN
// reachable states — `"expired"` was an eighth nothing ever set (a lapsed hold is
// `cancelled`, not `expired`) and the backend dropped it.
export const SESSION_STATES = [
  "scheduled", "in_progress", "computing", "computed", "captured",
  "void_no_show", "cancelled",
];

// Mirrors `schemas.SESSION_MONEY_FINAL_STATES`. The three states in which a session's
// MONEY will not change again: `captured` carries what Stripe took, the two void states
// carry a deliberate zero. `computed` is deliberately absent — a session whose capture is
// still pending (or whose capture the hold-sweeper is repairing) has NOT settled, even
// though the bill is already computed, and treating it as terminal is precisely the past
// bug (`useConsultStatus.js`) this constant now guards every reader against by being the
// ONE place the three names live. Every surface that gates a WRITE or a POLL on "is this
// consult over" reads this set, never a hand-copied literal.
export const SESSION_MONEY_FINAL_STATES = ["captured", "void_no_show", "cancelled"];

// Mirrors `schemas.CONSULT_AUTH_STATES` — the four-way summary `consults.auth_state`
// collapses seventeen payment states into, and the ONLY money vocabulary a patient's poll
// (`GET /consults/{id}/status`) or a practitioner's consult row publishes.
//
// It is a rendered family, not an internal one: a consult row whose room has never been
// opened carries `sessionStatus: null`, and `authState` is then the only thing that says
// whether the card behind it is authorised. `i18n/__tests__/keyCoverage.test.js` pins a
// `consult.list.auth.*` label per member against the Python declaration, so a fifth
// summary state cannot reach a practitioner as a raw identifier.
export const CONSULT_AUTH_STATES = ["none", "awaiting", "authorised", "failed"];

// Mirrors `schemas.JOIN_STATES` — WHEN a consult room may be entered, as
// `GET /consults/{id}/status` publishes it beside `opensAt` (contract §2). An ARRAY, and
// ordered in time, because the backend declares a tuple and
// `test_gateway_consult_join_window.py` compares the two artefacts element for element.
//
// A SEPARATE vocabulary from `CONSULT_AUTH_STATES`, deliberately: that one answers "is
// there money behind this consultation", this one answers "is it time". Collapsing both
// into `canJoin` is what let `/merci/consultation` mount a live room for an appointment
// 26 hours away with nothing on screen able to say why it should not have.
export const JOIN_STATES = ["not_yet_open", "open", "closed"];

// Mirrors the `presence.presentRoles` domain of `GET /consults/{id}/status`
// (docs/booking-module/telehealth-room-lifecycle.md §5). The `room_watch` job maps each
// Daily participant's `userId` — spelled `"{role}:{ref}"` — back to one of these two, so
// a room's occupancy is stated as WHO is there and not merely how many.
//
// A ROLE THIS BUILD DOES NOT KNOW MUST NOT REACH A SCREEN as a raw identifier; the array
// exists so a renderer can filter against it rather than print whatever arrived.
export const CONSULT_PRESENCE_ROLES = ["practitioner", "patient"];

// Mirrors `booking.schemas.BOOKING_PRICING_MODES` — HOW a consultation's money is
// computed, and the closed set of answers. Ordered as the backend tuple is, because
// `test_consult_status_one_shape.py` compares the two artefacts element for element.
//
// THE SERVER STATES THE MODE; NO SCREEN INFERS IT. Every payload that describes a
// consultation carries `pricingMode` — the in-room poll (`GET /consults/{id}/status`) and
// the practitioner's list row (`GET /tenant/consults`) alike. Two surfaces used to
// reconstruct it instead from `authState == null && !!bookingStatus`, which is reading an
// ABSENCE as a fact: a per-minute row whose payment lookup failed, or whose booking has
// been purged, has a null `authState` too and was bucketed and pilled as a paid fixed
// block. An absence has many causes; a stated mode has one.
export const CONSULT_PRICING_MODES = ["fixed", "per_minute"];
export const FIXED_PRICING_MODE = CONSULT_PRICING_MODES[0];
export const PER_MINUTE_PRICING_MODE = CONSULT_PRICING_MODES[1];

/**
 * Is this consultation sold as a FIXED BLOCK — paid in full at checkout, never metered?
 *
 * THE ONE PREDICATE, for both shapes. A status payload and a list row are different
 * projections of one consultation, and both name the model, so one function answers for
 * either. It was spelled six times across the SPA (twice byte-identically), which is how
 * the two list consumers came to disagree with the room screen about the same row.
 *
 * @param {?{pricingMode?: ?string}} row a `/status` payload or a `/tenant/consults` row
 */
export const isFixedBlock = (row) => row?.pricingMode === FIXED_PRICING_MODE;

/** Is this consultation METERED — a card hold, and a bill computed from the minutes? */
export const isPerMinute = (row) => row?.pricingMode === PER_MINUTE_PRICING_MODE;

// Mirrors `gateway.consults.PAID_BOOKING_STATUSES` — the appointment statuses under which
// a FIXED-BLOCK consultation has been paid for. It is the money answer for a model that
// has no card hold and therefore no `authState`: `bookingStatus` is the only field on
// `GET /consults/{id}/status` that says whether the charge went through.
//
// Rendered surfaces read this, never `joinState`, before saying anything about money. The
// two questions are separate — "is it paid" and "is it time" — and a screen that answered
// the second one first told a patient whose checkout was abandoned "Payment authorised —
// the room opens on ...", beside a booking row that said "Awaiting payment".
export const PAID_BLOCK_BOOKING_STATUSES = ["confirmed", "completed"];

/** True when a `GET /consults/{id}/status` payload describes a PAID fixed block. */
export const isPaidFixedBlock = (status) =>
  isFixedBlock(status)
  && PAID_BLOCK_BOOKING_STATUSES.includes(status?.bookingStatus);

/** The closed error-code set. Map these to i18n keys; never render the server message. */
export const ERROR_CODES = [
  "INSUFFICIENT_AVAILABLE", "PAYOUT_MINIMUM", "PAYOUT_FROZEN", "PAYOUT_DEBT_BLOCKED",
  "PAYOUT_DESTINATION_NOT_READY", "PAYOUT_NOT_PENDING", "ALREADY_RESERVED",
  "PRICING_NOT_SET", "RATE_OUT_OF_BAND", "PRICING_PENDING_APPROVAL",
  "PROVIDER_NOT_BILLABLE", "AUTH_NOT_READY", "ALREADY_COMPLETED", "NOTHING_TO_CAPTURE",
  // A patient may not cancel a consultation that is running: releasing the hold
  // mid-call would hand them the rest of the consult for free.
  "CONSULT_IN_PROGRESS",
  // The ceiling disclosure is the one fact a per-minute dispute turns on, so consent
  // is checked on the SERVER and refused here rather than trusted from the browser.
  "CONSENT_REQUIRED",
  // THE JOIN WINDOW (2026-09-03, F-15). The server decides WHEN a consult room may be
  // entered, and these are the two halves of that answer: `NOT_OPEN_YET` carries
  // `opensAt` on the refusal payload and means wait; `WINDOW_CLOSED` means there is
  // nothing to wait for. Two codes because they ask two different things of the reader.
  "CONSULT_NOT_OPEN_YET", "CONSULT_WINDOW_CLOSED", "CONSULT_NOT_PAID",
  // THE ROOM FAILED, WHICH IS NOT A MONEY ANSWER (2026-09-08). Both consult doors
  // reach a point where every gate the patient could act on has passed and the only
  // thing left that can fail is ours — no room row, or an adapter that minted no join
  // URL. Those were reported as `CONSULT_NOT_PAID` / `AUTH_NOT_READY`, so someone who
  // had paid in full was told their payment had not arrived. Not
  // `PAYMENT_PROVIDER_ERROR` either: that copy says "nothing was authorised", and for
  // a fixed block charged weeks earlier that is false too.
  "CONSULT_ROOM_UNAVAILABLE",
  // THE SEAT IS GONE (D7 verify round 10). An authorisation asked for an appointment
  // that is cancelled, or whose seat hold lapsed, or that was cancelled while its
  // checkout was being created: no page is handed out, because a hold landing there
  // would be on a seat nobody confirms. The patient books a new time.
  "CONSULT_NOT_PAYABLE",
  // `SCHEDULED_LANE_UNAVAILABLE` was retired 2026-08-27 when the >144 h lane reopened:
  // the T-48 h authorisation job now exists, so a card saved for a distant slot is
  // actually charged. A code nothing can raise is drift, so it left all six mirrors
  // together. The lane's real failures are AUTH_EXPIRED / CARD_DECLINED_OFF_SESSION.
  // `USE_CONSULT_COMPLETE` / `USE_CONSULT_DOOR` were retired 2026-09-02 with the legacy
  // prepaid telehealth lane they refused traffic into (the legacy room-complete and
  // prepaid-join endpoints they guarded no longer exist, so nothing can raise them).
  "AUTH_EXPIRED", "CARD_DECLINED_OFF_SESSION",
  "CART_EMPTY", "CART_ITEM_INVALID", "CART_MIXED_CURRENCY",
  "CART_TENANT_NOT_PAYABLE", "CART_TOO_MANY_LINES", "CART_MULTI_TENANT_DISABLED",
  "REFUND_EXCEEDS_CAPTURED", "REFUND_AMOUNT_INVALID",
  "PAYMENT_NOT_REFUNDABLE", "NOTHING_TO_REVERSE",
  "PAYMENT_NOT_FOUND",
  // An operator write whose practice is named by the URL (adjustment, reserve policy)
  // names none that exists: refused before anything is written or the step-up is spent.
  "TENANT_NOT_FOUND",
  "COMMISSION_RULE_CONFLICT",
  "ADJUSTMENT_INVALID", "SETTINGS_INVALID", "REAUTH_REQUIRED", "PAYOUT_FREQUENCY_CAPPED",
  // The settlement currency may not move while affiliate commissions booked in the
  // old one are still reversible: the ledger refuses a group in any other currency,
  // so their future clawbacks would simply be unbookable. `fields` carries the count.
  "SETTLEMENT_CURRENCY_LOCKED_BY_AFFILIATE_PAYABLES",
  // Stripe rejected the request or could not be reached, and NOTHING was authorised.
  // Added 2026-09-03 (F-07): a raw Stripe exception used to reach the wire as
  // `{"code": null}` — a `stripe.StripeError` has a `code` attribute whose value is null
  // for an idempotency error — so the patient read "an unexpected error occurred".
  "PAYMENT_PROVIDER_ERROR",
  // The operator rebuild endpoint's two failures, split apart 2026-09-01. Both used to
  // answer ALREADY_RESERVED — "already reserved by another withdrawal" — including for a
  // balance document nobody had reserved anything against, which is how an unrebuildable
  // document stayed invisible. CONTENDED means press it again; UNREPAIRABLE means the
  // document itself cannot satisfy the compare-and-set and needs a human.
  "BALANCE_REBUILD_CONTENDED", "BALANCE_DOC_UNREPAIRABLE",
];

/**
 * Sign convention for the trial balance (contract §4, mirrors `schemas.py`): `accounts`
 * in the wire response are SIGN-CORRECTED ("natural") balances — positive means "more of
 * what this account is for" — and must never be summed directly. `driftCents` is
 * `Σ(balance × ACCOUNT_SIGN[account])`, which collapses to `Σ(debits − credits)`, the
 * quantity double-entry actually forces to zero. Exported so `TrialBalancePanel` can
 * render debit/credit intent per row without re-deriving this mapping itself.
 * +1 = debit-normal (assets, expenses/losses); -1 = credit-normal (liabilities, equity,
 * revenue).
 */
export const ACCOUNT_SIGN = {
  platform_cash: 1,
  tenant_payable: -1,
  platform_revenue_commission: -1,
  platform_revenue_subscription: -1,
  stripe_fees: 1,
  refunds_payable: -1,
  dispute_losses: 1,
  tenant_adjustments: -1,
  platform_adjustment_expense: 1,
  platform_equity_opening: -1,
  giftcard_liability: -1,
  // IN THE SAME EDIT AS `LEDGER_ACCOUNTS`, for the reason the Python dict states: an
  // account the sign map does not name is a row `TrialBalancePanel` would label with the
  // wrong normal side, and a balance `driftCents` would weigh the wrong way.
  affiliate_payable: -1,
  platform_affiliate_expense: 1,
};

// ── typedefs ───────────────────────────────────────────────────────────────────
/**
 * @typedef {Object} TenantBalance
 * @property {string} currency
 * @property {number} pendingCents          inside the hold window
 * @property {number} availableCents        withdrawable now (guard field, net of claims)
 * @property {number} reservedCents         claimed by an in-flight payout/freeze
 * @property {number} lifetimePaidCents
 * @property {number} adjustmentsCents      negative = the practice owes Solunary
 * @property {number} debtCents
 * @property {boolean} frozen
 * @property {{amountCents:number, releasesAt:string}[]} holds  per-hold release dates
 * @property {string} lastRebuiltAt
 * @property {TenantBalanceLimits} [limits]  ADVISORY: the server refuses regardless
 */

/**
 * The advisory block. Every member exists so a client can STATE a platform rule instead
 * of hardcoding a guess, and no member is an authorisation: the server's own refusal
 * codes stay the only authority, and an absent field means "say nothing".
 *
 * @typedef {Object} TenantBalanceLimits
 * @property {?number} minPayoutCents
 * @property {?number} autoApproveMaxCents        null = no upper bound (§16.5/§16.6)
 * @property {number} frequencyCapPerWeek         0 = uncapped
 * @property {?number} commissionBps              resolved for this tenant
 * @property {?number} rollingReservePct
 * @property {?number} rollingReserveDays
 * @property {?string} reserveWindowEndsAt        null = nothing would be held now
 * @property {boolean} [crossBorder]
 * @property {boolean} [payoutDestinationReady]   ABSENT = the server states no verdict;
 *   `false` = the payout gate would refuse this destination today. Never inferred.
 * @property {?string} [payoutDestinationBlockedReason] one of `connect_readiness`'s
 *   closed set (no_account | payouts_disabled | transfers_inactive |
 *   requirements_past_due); null when ready. Geography is DELIBERATELY absent from it.
 * @property {?PayoutConversion} [payoutConversion] null = the practice operates in the
 *   settlement currency and nothing converts at payout (SHEET-212)
 */

/**
 * What converts when a practice pricing in another currency is paid out of the
 * settlement balance. The ratio is `fx.py`'s implied one (settled / presentment) over
 * the practice's own captures; both integers are null when it has captured nothing, and
 * no local figure may then be shown. `feeBps` is the platform's STATED conversion fee
 * rate (`payouts.PAYOUT_CONVERSION_FEE_BPS`), present on every converting statement.
 *
 * @typedef {Object} PayoutConversion
 * @property {string} currency           the practice's operating currency, lowercase ISO
 * @property {?number} presentmentCents  summed sale, in `currency`
 * @property {?number} settledCents      the same sales as Stripe settled them
 * @property {number} feeBps             positive integer basis points
 */

/**
 * A payment as SOME surface ships it. The optional properties are not "sometimes
 * missing" — each one is present on exactly the audiences that may read it, and the
 * backend builds all three rows by extending one base (`schemas.payment_row`):
 *
 *   public poll   GET /api/payments/{id}          base + holdReleasesAt
 *   practitioner  GET /api/tenant/gateway/…       base + the sale, its margin,
 *                                                 commissionRateBps, customerEmail,
 *                                                 createdAt, holdReleasesAt
 *   operator      GET /api/super-admin/gateway/…  base + the sale, its margin,
 *                                                 createdAt, tenantId
 *
 * The base is the first seven properties below. A margin or a buyer's email on the
 * PUBLIC poll would be a defect, not a richer payload.
 *
 * @typedef {Object} Payment
 * @property {string} paymentId
 * @property {string} status                one of PAYMENT_STATES ("" if never set)
 * @property {string} currency              never null: the settlement default applies
 * @property {number} amountAuthorisedCents
 * @property {number} amountCapturedCents
 * @property {number} refundedCents
 * @property {?string} capturedAt
 * @property {string} [type]                subscription|marketplace|telehealth
 * @property {string} [productType]
 * @property {number} [commissionAmountCents]
 * @property {number} [amountNetToTenantCents]
 * @property {?number} [commissionRateBps]  practitioner only; null = never resolved
 * @property {?string} [customerEmail]      practitioner only
 * @property {?string} [createdAt]          authenticated surfaces only
 * @property {string} [tenantId]            operator only
 * @property {?{currency: string, capturedCents: number, commissionCents: number,
 *             netToPracticeCents: number}} [settled]
 *                                          operator only: the capture LEDGER group in the
 *                                          settlement currency; null = nothing booked
 * @property {?string} [holdReleasesAt]     absent from the operator's row
 */

/**
 * @typedef {Object} PayoutRequest
 * @property {string} payoutRequestId
 * @property {number} amountCents
 * @property {string} currency
 * @property {string} status                one of PAYOUT_STATES
 * @property {boolean} [autoApproved]
 * @property {?string} requestedAt
 * @property {?string} completedAt
 * @property {?string} failureCode
 * @property {?string} failureMessage
 */

/**
 * @typedef {Object} ConsultQuote
 * @property {number} ratePerMinuteCents
 * @property {number} baseFeeCents         additive, charged once; 0 on a rate row that
 *                                         sets none, and never charged on a no-show
 * @property {number} minBillableMinutes
 * @property {number} maxBillableMinutes
 * @property {number} ceilingCents          what the patient consents to at booking
 * @property {number} noShowFeeCents        FLAT fee (§16.4), not a percentage
 * @property {number} rescheduleMinNoticeHours
 * @property {string} currency
 * @property {string} lane                  immediate|scheduled
 * @property {string} authState             none|awaiting|authorised|failed
 * @property {string[]} disclosureKeys
 */

/**
 * The `presence` sub-document of `GET /consults/{id}/status`
 * (docs/booking-module/telehealth-room-lifecycle.md §5). Written ONLY by the `room_watch`
 * job, and only after a SUCCESSFUL read of Daily: a failed read makes no writes at all,
 * because a 502, a rate-limit and "one participant present" are indistinguishable from
 * the outside and ejecting on absent evidence disconnects a live consultation.
 *
 * `snapshotAt` is therefore the age of every other field here, and it is up to one job
 * interval (60 s) old. Nothing rendered from this document may claim finer resolution
 * than that — which is why the solo notice states whole minutes and never seconds.
 *
 * Always present on the payload, with `presentRoles: []` / `presentCount: 0` before the
 * job has ever looked: a key that disappears is a second wire shape.
 *
 * @typedef {Object} ConsultPresence
 * @property {?string} snapshotAt        when `room_watch` last read Daily successfully
 * @property {string[]} presentRoles     subset of CONSULT_PRESENCE_ROLES
 * @property {number} presentCount
 * @property {?string} soloSince         start of the CURRENT uninterrupted solo run
 * @property {?string} soloDisconnectAt  max(soloSince, startsAt) + soloTimeoutMinutes
 * @property {?string} soloEjectedAt     `at` of the LAST solo ejection; append-only
 */

/**
 * The room-lifecycle keys of `GET /consults/{id}/status`
 * (docs/booking-module/telehealth-room-lifecycle.md §5), read by `gateway/roomPhase.js`
 * and by nothing else. Documented apart from the rest of the payload because they are
 * one fact together: the three instants that decide which sentence a room screen shows.
 *
 * ALWAYS PRESENT, NULL RATHER THAN ABSENT. A null removes a boundary — the client stops
 * asking that question — and is never a licence to compute the boundary locally:
 * `closesAt` is `scheduledEndAt + hardCloseGraceMinutes`, and that grace is an
 * operator-editable setting the browser does not hold.
 *
 * `now` is the SERVER's clock, refreshed on every poll. A room screen keeps
 * `offset = now − Date.now()` from it and compares instants against `Date.now() + offset`,
 * so a browser whose own clock is minutes out does not close a room early or hold one
 * open past its end.
 *
 * `closesAt` is the HARD CLOSE and there is no second name for it: the join window's
 * closing edge, the completion sweeper's deadline and the Daily room/token `exp` are all
 * this one instant.
 *
 * @typedef {Object} ConsultRoomLifecycle
 * @property {string} now                server clock at the moment of the read
 * @property {?string} scheduledEndAt    slot start + the booked duration
 * @property {?string} endingSoonAt      scheduledEndAt − endingSoonNoticeMinutes
 * @property {?string} closesAt          scheduledEndAt + hardCloseGraceMinutes
 * @property {ConsultPresence} presence
 */

/**
 * @typedef {Object} ConsultComputation
 * @property {number} billableMinutes
 * @property {number} grossCents
 * @property {number} taxCents
 * @property {number} commissionCents
 * @property {number} netCents
 * @property {number} capturedCents
 * @property {number} releasedCents         the unused part of the hold, given back
 * @property {string} outcome
 * @property {string} evidenceQuality       one of EVIDENCE_GRADES
 */

/**
 * One row of `GET /super-admin/consults` (contract-frozen 2026-09-02, docs/payment-module/
 * gateway-api-contract.md around the `GET /api/super-admin/consults` line — that section
 * names the endpoint but carries no example row; this typedef is the shape until it does).
 * ALL camelCase — `super_admin/GatewayConsole.jsx`'s `ConsultsTab` used to read this as the
 * snake_case `consult_repo` storage shape (`s.billable_minutes ?? "—"`), which rendered
 * "0" for every row because the field it read never existed on the response at all.
 *
 * `sessionId` is NOT `appointmentId`: the drill-down
 * (`GET /super-admin/consults/{session_id}/timeline`) and every row key must use
 * `sessionId`, the object this row actually is. `billableMinutes` is `null` until the
 * session's clock finishes — never a default `0` — so a genuine no-show's `0` and an
 * unmeasured session's absence stay distinguishable.
 * @typedef {Object} SuperAdminConsultRow
 * @property {string} sessionId
 * @property {string} appointmentId
 * @property {string} tenantId
 * @property {string} tenantName
 * @property {?string} sessionStatus        one of SESSION_STATES; null for a FIXED BLOCK,
 *                                        which never opens a session
 * @property {?string} bookingStatus        the appointment's own status — the ONLY status a
 *                                        fixed block has, since it has neither a session
 *                                        nor an authorisation
 * @property {string} pricingMode          one of CONSULT_PRICING_MODES — WHICH MODEL sold
 *                                        this consultation, stated by the server on every
 *                                        row so that no screen infers it from the nulls
 *                                        below (read it through `isFixedBlock`)
 * @property {?string} authState            null for a fixed block: "this question does not
 *                                        apply", which is not the same as "none". It is
 *                                        ALSO null for a metered row whose live payment
 *                                        could not be read, which is why it is not a
 *                                        model discriminator
 * @property {?string} outcome
 * @property {?string} scheduledStartAt
 * @property {?string} scheduledEndAt
 * @property {?string} computedAt
 * @property {?number} billableMinutes      null until computed; a no-show's 0 is real
 * @property {?number} capturedCents        null until captured
 * @property {string} currency
 * @property {string} evidenceQuality       one of EVIDENCE_GRADES
 * @property {?string} patientName
 * @property {?string} patientEmail
 * @property {?string} serviceName
 * @property {?string} capReachedAt
 * @property {?string} receiptNumber        null until a receipt has been issued
 */

// ── mock fixtures ──────────────────────────────────────────────────────────────
const ISO = "2026-08-21T12:00:00+00:00";

/** @returns {TenantBalance} */
export const mockTenantBalance = (o = {}) => ({
  currency: SETTLEMENT_CURRENCY,
  pendingCents: 15200,
  availableCents: 121600,
  reservedCents: 0,
  lifetimePaidCents: 413000,
  adjustmentsCents: 0,
  debtCents: 0,
  frozen: false,
  holds: [{ amountCents: 12160, releasesAt: "2026-08-28T12:00:00+00:00" }],
  // Projected from `gateway_settings` — the tenant surface's only view of the platform
  // floor/ceiling. `frequencyCapPerWeek: 0` means uncapped. A caller that gets a response
  // with no `limits` at all must disable the checks this powers, never substitute a
  // guessed number and present it to a practitioner as fact.
  // `autoApproveMaxCents: null` = NO upper bound: §16.5/§16.6 answered that every
  // eligible request (>= the $100 minimum) auto-approves and there is no manual gate by
  // default. A number reinstates one above it, which is why the field survives.
  // `commissionBps` — ADDED 2026-08-28 — is the rate RESOLVED for this tenant, and is the
  // only place a practitioner can read what the gateway charges them. Advisory like the
  // rest of the block: a client that receives no rate states nothing, never a constant.
  // The WITHDRAWAL DISCLOSURE — ADDED 2026-08-28 (spec §3/§8/§12). The rolling reserve
  // is taken OUT of a payout and the cross-border fee is borne by the tenant, and
  // neither is computable client-side: the reserve is resolved per tenant and dated
  // from that tenant's first money fact. `reserveWindowEndsAt: null` is the server's
  // single answer to "nothing would be held right now" — no history, elapsed window, or
  // a 0 % override — so the form renders the held portion on its presence alone.
  // `crossBorder` says only WHETHER the fee applies: the amount is copied from Stripe's
  // balance transaction after the transfer and is unknown at this point.
  // `payoutDestinationReady` — ADDED 2026-09-10 (BUG-222) — is
  // `payouts.validate_payout_eligibility`'s own destination predicate
  // (`modules/payments/connect_readiness.py`) reported, so the withdrawal form can
  // refuse BEFORE the confirmation dialog instead of after it. `false` carries a
  // `payoutDestinationBlockedReason` from that module's closed set; ABSENT means the
  // server does not compute the verdict, which a client must read as "say nothing and
  // let the server refuse", never as "not ready".
  // `payoutConversion` — ADDED 2026-09-23 (SHEET-212) — null for a practice operating
  // in the settlement currency, which is what this default fixture is.
  limits: { minPayoutCents: 10000, autoApproveMaxCents: null, frequencyCapPerWeek: 0,
            commissionBps: 2000,
            rollingReservePct: 10, rollingReserveDays: 90,
            reserveWindowEndsAt: "2026-11-20T00:00:00+00:00", crossBorder: false,
            payoutDestinationReady: true, payoutDestinationBlockedReason: null,
            payoutConversion: null },
  lastRebuiltAt: ISO,
  ...o,
});

/** @returns {PayoutRequest} */
export const mockPayoutRequest = (o = {}) => ({
  payoutRequestId: "pr_1",
  amountCents: 5000,
  currency: SETTLEMENT_CURRENCY,
  status: "requested",
  autoApproved: true,
  requestedAt: ISO,
  completedAt: null,
  failureCode: null,
  failureMessage: null,
  ...o,
});

/**
 * The SUPER-ADMIN queue row: `mockPayoutRequest` plus `tenantId`.
 *
 * A separate factory because the two surfaces are not the same shape and once were
 * assumed to be. `GET /super-admin/gateway/payouts/pending` used to return the
 * persistence document — keyed `id` — while the console read `payoutRequestId`, so every
 * field it rendered was `undefined` and the approve URL carried the literal string
 * "undefined". `schemas.admin_payout_row` is the backend half of this pairing.
 *
 * @returns {PayoutRequest & {tenantId: string}}
 */
export const mockAdminPayoutRequest = (o = {}) => ({
  ...mockPayoutRequest(),
  tenantId: "cabinet-luna",
  ...o,
});

export const mockPayouts = (o = {}) => ({
  rows: [
    mockPayoutRequest(),
    mockPayoutRequest({ payoutRequestId: "pr_0", status: "paid", completedAt: ISO }),
  ],
  ...o,
});

/**
 * Sign-corrected ("natural") balances, per §4: a liability we owe (`tenant_payable`)
 * reads +121600, not the raw debit−credit −121600. Balanced by construction — verify with
 * `Σ(balance × ACCOUNT_SIGN[account])`, not with a plain sum. `driftCents`/`sumZero` are
 * the ONLY fields a consumer should alarm on; pass `{driftCents, sumZero: false}` as an
 * override to get the unbalanced variant for testing the alarm path.
 */
export const mockTrialBalance = (o = {}) => ({
  accounts: {
    platform_cash: 2516362,
    tenant_payable: 121600,
    platform_revenue_commission: 23600,
    platform_revenue_subscription: 400000,
    stripe_fees: 4710,
    refunds_payable: 0,
    dispute_losses: 0,
    tenant_adjustments: 0,
    platform_adjustment_expense: 0,
    platform_equity_opening: 1975872,
  },
  driftCents: 0,
  sumZero: true,
  // The ledger settles in exactly ONE currency, and it travels with the amounts: the
  // console cannot know it otherwise, and it read a hardcoded "cad" from the component
  // until this field existed.
  currency: SETTLEMENT_CURRENCY,
  ...o,
});

